/**
 * FeedbackPanel — Feedback tab content.
 *
 * Plain textarea + Submit for natural-language coding corrections.
 * On success, calls onApplied() so the parent can bump codingRefreshTick,
 * causing CodingPanel to re-poll and show the updated codes.
 */
import React, { useState, useCallback } from 'react';
import { apiClient } from '../services/api';

interface FeedbackPanelProps {
  sessionId: string;
  // The parent receives the updated chart text (when present) and any new
  // codes returned by the feedback endpoint, so it can refresh its note
  // pane and bump the CodingPanel refreshTick in one go.
  onApplied?: (update: {
    note_text?: string;
    note_version?: number;
    routes_fired?: string[];
  }) => void;
}

export function FeedbackPanel({ sessionId, onApplied }: FeedbackPanelProps) {
  const [feedbackText, setFeedbackText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // 'applied' = the bundle re-ran and codes changed (status done/needs_resolution/
  // degraded/completed); 'noted' = acknowledgment only, nothing changed.
  const [lastResult, setLastResult] = useState<{ status: 'applied' | 'noted'; appliedAt?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    const text = feedbackText.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const resp = await apiClient.submitCodingFeedback(sessionId, text);
      const applied = resp.status !== 'noted';
      setLastResult({ status: applied ? 'applied' : 'noted', appliedAt: resp.applied_at });
      setFeedbackText('');
      if (applied) {
        onApplied?.({
          note_text: resp.note_text,
          note_version: resp.note_version,
          routes_fired: resp.routes_fired,
        });
      }
    } catch (e: any) {
      // 409 = open clarification queries must be answered first — surface the
      // backend's message verbatim (it tells the user to use the Billing tab).
      setError(e?.message || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  }, [sessionId, feedbackText, submitting, onApplied]);

  return (
    <div style={panelStyle}>
      <div style={labelStyle}>Tell the coder what to change.</div>
      <div style={hintStyle}>
        Examples: "Change I10 to I11.9. Patient also has T2DM." or "47562 should be 47563."
      </div>
      <textarea
        value={feedbackText}
        onChange={(e) => setFeedbackText(e.target.value)}
        disabled={submitting}
        placeholder="Type your correction here…"
        rows={4}
        style={textareaStyle}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && feedbackText.trim().length >= 3) {
            handleSubmit();
          }
        }}
      />
      <div style={footerStyle}>
        <button
          onClick={handleSubmit}
          disabled={submitting || !feedbackText.trim()}
          style={submitBtnStyle(submitting || !feedbackText.trim())}
        >
          {submitting ? 'Processing…' : 'Submit'}
        </button>

        {lastResult && lastResult.status === 'applied' && (
          <span style={successStyle}>
            Updated — see Billing codes
            {lastResult.appliedAt && (
              <> · {new Date(lastResult.appliedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>
            )}
          </span>
        )}

        {lastResult && lastResult.status === 'noted' && (
          <span style={notedStyle}>Noted — no changes applied</span>
        )}

        {error && (
          <span style={errorStyle}>{error}</span>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────

const panelStyle: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  background: '#fff',
  padding: 16,
};

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: '#111827',
  marginBottom: 4,
};

const hintStyle: React.CSSProperties = {
  fontSize: 11,
  color: '#6b7280',
  marginBottom: 10,
  lineHeight: 1.5,
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 10px',
  fontSize: 13,
  fontFamily: 'inherit',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  resize: 'vertical',
  minHeight: 72,
  color: '#111827',
};

const footerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginTop: 8,
  flexWrap: 'wrap',
};

function submitBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: '7px 18px',
    borderRadius: 6,
    border: 'none',
    background: disabled ? '#9ca3af' : '#1e293b',
    color: '#fff',
    fontWeight: 600,
    fontSize: 13,
    cursor: disabled ? 'default' : 'pointer',
  };
}

const successStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#15803d',
  fontWeight: 500,
};

const notedStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#6b7280',
};

const errorStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#b91c1c',
};
